import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, EASE } from "./shared";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
      className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/90 backdrop-blur-xl border-[#1B1915]/10"
          : "bg-transparent border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-[padding] duration-500 ${
          scrolled ? "py-2.5" : "py-5"
        }`}
      >
        <a href="#home" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img
            src="/assets/logo.png"
            alt="ELYNCOR ELEMENTS logo"
            className={`transition-[width,height] duration-500 mix-blend-multiply ${
              scrolled ? "w-8 h-8" : "w-11 h-11"
            }`}
            data-testid="nav-logo-image"
          />
          <span
            className={`font-serif-display tracking-[0.14em] text-[#1B1915] transition-[font-size] duration-500 ${
              scrolled ? "text-sm" : "text-base sm:text-lg"
            }`}
          >
            ELYNCOR ELEMENTS
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {NAV_LINKS.slice(1, 6).map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[13px] tracking-wide text-[#1A201C]/70 hover:text-[#1B1915] transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#enquire"
            className="ml-2 text-[12px] tracking-[0.18em] uppercase px-5 py-2.5 rounded-full bg-[#1B1915] text-[#FDFBF7] hover:bg-[#2C2822] transition-colors duration-300"
            data-testid="nav-enquire-now-button"
          >
            Enquire Now
          </a>
        </nav>

        <button
          className="lg:hidden text-[#1B1915] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-menu-button"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="lg:hidden overflow-hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#1B1915]/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-wide text-[#1A201C]/80"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#enquire"
                onClick={() => setOpen(false)}
                className="mt-2 text-center text-[12px] tracking-[0.18em] uppercase px-5 py-3 rounded-full bg-[#1B1915] text-[#FDFBF7]"
                data-testid="nav-mobile-enquire-button"
              >
                Enquire Now
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
